import { useState, useMemo, useCallback, useRef, memo, forwardRef, useImperativeHandle } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  ColumnDef,
  ColumnResizeMode,
  OnChangeFn,
  ColumnSizingState,
  Updater,
} from '@tanstack/react-table';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Alert,
  TextField,
} from '@mui/material';
import { CheckBox as CheckBoxIcon } from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import Images from '@/assets/Images';
import { PAGE_SIZE_OPTIONS } from '@/config/constants';
import { DATE_FORMATS } from '@/utils/constants';
import usePermissions from '@/hooks/usePermissions';
import ConfirmDeleteDialog from './ConfirmDeleteDialog';
import ColumnCustomizer from './ColumnCustomizer';
import ViewManager from './ViewManager';
import { TableViewProps } from './table.types';
import { useSchemaToHeaders } from './useSchemaToHeaders';
import { useTableView } from './hooks';
import useGridCustomizationWithAPI from './useGridCustomizationWithAPI';

type TanStackTableViewWithSchemaProps<T> = Omit<TableViewProps<T>, 'headerDetails'> & {
  entity: string;
  enableAPIConfig?: boolean;
  onFilterChange?: {
    (key: string, value: any): void;
    (updates: Record<string, any>): void;
  };
  filterValues?: Record<string, any>;
  fallbackHeaders?: TableViewProps<T>['headerDetails']; // Fallback headers when API schema is not available
  initialView?: string;
  extraColumns?: ColumnDef<T>[];
  handleApprovals?: (id: string | number) => void;
  renderRowActions?: (row: T) => React.ReactNode;
  selectColumn?: {
    headerCell?: React.ReactNode;
    renderCell: (row: T, index: number) => React.ReactNode;
    width?: number;
  };
  renderActions?: (row: T, index: number) => React.ReactNode;
  isEditDisabled?: (row: T) => boolean;
};

export interface TanStackTableViewWithSchemaRef {
  getGridView: () => {
    visible_columns: string[];
    column_widths: Record<string, number>;
    current_view?: string;
  };
}

const columnResizeMode: ColumnResizeMode = 'onChange';

function TanStackTableViewWithSchemaInner<T>({
  data,
  loading = false,
  emptyMessage = 'No records found',
  rowKey,
  onRowClick,
  maxHeight = 'calc(100vh - 200px)',
  renderToolbar,
  sortKey,
  sortDirection = 'asc',
  onSortChange,
  page = 0,
  rowsPerPage = 10,
  totalCount = 0,
  rowsPerPageOptions = PAGE_SIZE_OPTIONS,
  onPageChange,
  onRowsPerPageChange,
  disablePagination = false,
  handleView,
  handleEdit,
  handleApprovals,
  handleDelete,
  confirmDelete,
  permissionModule,
    entity,
  enableAPIConfig = true,
  onFilterChange,
  filterValues = {},
  fallbackHeaders = [],
  initialView,
  selectColumn,
  extraColumns = [],
  renderActions,
  renderRowActions,
  isEditDisabled,

}: TanStackTableViewWithSchemaProps<T>, ref: React.Ref<TanStackTableViewWithSchemaRef>) {
  const showSelectColumn = Boolean(selectColumn);
  const selectColumnWidth = selectColumn?.width ?? 60;
  const { canView, canEdit, canDelete } = usePermissions();

  const hasViewPermission = !permissionModule || canView(permissionModule);
  const hasEditPermission = !permissionModule || canEdit(permissionModule);
  const hasDeletePermission = !permissionModule || canDelete(permissionModule);

  const showView = handleView && hasViewPermission;
  const showEdit = handleEdit && hasEditPermission;
  const showApprovals = handleApprovals && hasEditPermission;
  const showDelete = handleDelete && hasDeletePermission;
  const showActions = Boolean(showView || showEdit || showDelete || renderRowActions || renderActions);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | number | null>(null);

  // Use API-enabled grid customization
  const {
    columns: gridColumns,
    setVisibility,
    setWidth,
    moveColumn,
    resetToSchema,
    savePreferencesToAPI,
    resetPreferencesToAPI,
    schemaLoading,
    schemaError,
    hasAPISchema,
    activeSchema,
    // View management
    views,
    currentView,
    switchView,
    createView,
    removeView,
    viewsLoading,
  } = useGridCustomizationWithAPI({
    entity,
    enableAPIConfig,
    currentView: initialView,
  });

  // Expose grid view state through ref
  useImperativeHandle(ref, () => ({
    getGridView: () => {
      const visibleColumns = gridColumns.filter(col => col.visible);
      return {
        visible_columns: visibleColumns.map(col => col.key),
        column_widths: visibleColumns.reduce((acc, col) => {
          acc[col.key] = col.width;
          return acc;
        }, {} as Record<string, number>),
        current_view: currentView,
      };
    },
  }), [gridColumns, currentView]);

  // Debounced save function for column preferences
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  const debouncedSavePreferences = useCallback(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    saveTimeoutRef.current = setTimeout(() => {
      if (hasAPISchema && savePreferencesToAPI) {
        savePreferencesToAPI().catch(error => {
          console.error('Failed to auto-save column preferences:', error);
        });
      }
    }, 1000); // 1 second delay to batch multiple changes
  }, [hasAPISchema, savePreferencesToAPI]);

  // Convert schema to headers with dynamic filter data, or use fallback headers
  // Always call the hook with a stable reference to avoid conditional hook calls
  // Create visibility overrides from grid columns for user preferences
  const visibilityOverrides = useMemo(() => {
    const overrides: Record<string, boolean> = {};
    gridColumns.forEach(col => {
      overrides[col.key] = col.visible;
    });
    return overrides;
  }, [gridColumns]);

  const { headers: schemaHeaders } = useSchemaToHeaders({
    schema: activeSchema || null,
    onFilterChange,
    filterValues,
    visibilityOverrides,
  });

  // Use schema headers if available, otherwise use fallback headers
  const headerDetails = activeSchema ? schemaHeaders : fallbackHeaders;

  const [activeFilterKey, setActiveFilterKey] = useState<string | null>(null);

  // Use table view hook for filter UI functionality
  const {
    makeHeader,
    activeDateColumn,
    activeNumberRangeColumn,
    dateFilterKey,
    numberRangeFilterKey,
    pendingDateRange,
    pendingNumberRange,
    setDateFilterKey,
    setNumberRangeFilterKey,
    setPendingDateRange,
    setPendingNumberRange,
  } = useTableView({
    headerDetails,
    rowKey,
    onPageChange,
    onRowsPerPageChange,
    onSortChange,
    sortKey,
    sortDirection,
    loading,
    activeFilterKey,
    setActiveFilterKey,
  });

  // Map headerDetails by key for quick lookup
  const headerMap = useMemo(() => {
    const map = new Map<string, typeof headerDetails[0]>();
    headerDetails.forEach((h) => map.set(h.key, h));
    return map;
  }, [headerDetails]);

  // Build TanStack column defs from visible grid columns (in order) or fallback headers
  const tanstackColumns = useMemo<ColumnDef<T>[]>(() => {
    let baseCols: ColumnDef<T>[];

    if (activeSchema) {
      // Use schema-based columns
      baseCols = gridColumns
        .filter((gc) => gc.visible)
        .map((gc) => {
          const header = headerMap.get(gc.key);
          return {
            id: gc.key,
            accessorKey: gc.key,
            size: gc.width,
            minSize: 80,
            maxSize: 500,
            header: gc.label,
            enableResizing: true,
            cell: ({ row }: { row: any }) => {
              if (header?.render) return header.render(row.original, row.index);
              const val = (row.original as Record<string, unknown>)[gc.key];
              if (val === null || val === undefined || val === '') return '-';

              if (Array.isArray(val)) {
                if (val.length === 0) return '-';
                const labels = val.map(item => {
                  if (typeof item === 'object' && item !== null) {
                    return (item as any).label || (item as any).name || String(item);
                  }
                  return String(item);
                }).filter(Boolean);
                return labels.length > 0 ? labels.join(', ') : '-';
              }

              if (typeof val === 'object' && val !== null) {
                const obj = val as any;
                if (obj.label) return String(obj.label);
                if (obj.name) return String(obj.name);
                return String(val);
              }
              
              if (typeof val === 'number' && Number.isFinite(val)) {
                return val.toLocaleString('en-US');
              }
              return val as React.ReactNode;
            },
          };
        });
    } else {
      // Use fallback headers
      baseCols = fallbackHeaders.map((header) => ({
        id: header.key,
        accessorKey: header.key,
        size: typeof header.width === 'string' ? parseInt(header.width, 10) || 150 : (header.width ?? 150),
        minSize: 80,
        maxSize: 500,
        header: header.label,
        enableResizing: true,
        cell: ({ row }: { row: any }) => {
          if (header.render) return header.render(row.original, row.index);
          const val = (row.original as Record<string, unknown>)[header.key];
          if (val === null || val === undefined || val === '') return '-';

          if (Array.isArray(val)) {
            if (val.length === 0) return '-';
            const labels = val.map(item => {
              if (typeof item === 'object' && item !== null) {
                return (item as any).label || (item as any).name || String(item);
              }
              return String(item);
            }).filter(Boolean);
            return labels.length > 0 ? labels.join(', ') : '-';
          }

          if (typeof val === 'object' && val !== null) {
            const obj = val as any;
            if (obj.label) return String(obj.label);
            if (obj.name) return String(obj.name);
            return String(val);
          }
          
          if (typeof val === 'number' && Number.isFinite(val)) {
            return val.toLocaleString('en-US');
          }
          return val as React.ReactNode;
        },
      }));
    }

    return [...baseCols, ...extraColumns];
  }, [activeSchema, gridColumns, headerMap, fallbackHeaders, extraColumns]);

  const table = useReactTable({
    data,
    columns: tanstackColumns,
    columnResizeMode,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    enableColumnResizing: true,
    onColumnSizingChange: (updater: Updater<ColumnSizingState>) => {
      // Handle column sizing changes
      if (typeof updater === 'function') {
        const currentSizing = table.getState().columnSizing;
        const newSizing = updater(currentSizing);
        // Update column widths in the grid customization
        Object.entries(newSizing).forEach(([columnId, width]) => {
          if (typeof width === 'number') {
            setWidth(columnId, width);
          }
        });
        
        // Auto-save preferences after resize using debounced function
        debouncedSavePreferences();
      }
    },
  });

  const getRowId = (row: T, index: number) => {
    if (rowKey) return String(rowKey(row, index));
    return String((row as { id?: string | number })?.id ?? index);
  };

  const getActionId = (row: T, index: number): string | number | undefined => {
    if (rowKey) {
      const k = rowKey(row, index);
      if (typeof k === 'string' || typeof k === 'number') return k;
    }
    return (row as { id?: string | number })?.id;
  };

  const handleDeleteClick = (id: string | number) => {
    if (!handleDelete) return;
    if (confirmDelete) {
      setPendingDeleteId(id);
      setDeleteDialogOpen(true);
      return;
    }
    handleDelete(id);
  };

  const visibleGridColumns = activeSchema ? gridColumns.filter((c) => c.visible) : fallbackHeaders;

  // Show loading state while schema is loading
  if (schemaLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Show error state if schema failed to load
  if (schemaError) {
    return (
      <Alert severity="error">
        Failed to load table configuration. Please try again later.
      </Alert>
    );
  }

  // Show message if no schema is available and no fallback headers
  if (!activeSchema && !fallbackHeaders.length) {
    return (
      <Alert severity="info">
        No table configuration available for this entity.
      </Alert>
    );
  }

  return (
    <Box
      sx={{
        mt: 3,
        // Global override for dropdown menus in this table
        '& .MuiPopover-root': {
          zIndex: '1400 !important',
        },
        '& .MuiMenu-root': {
          zIndex: '1400 !important',
        },
        '& .MuiAutocomplete-popper': {
          zIndex: '1400 !important',
        },
        '& .MuiPopper-root': {
          zIndex: '1400 !important',
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 1, gap: 1 }}>
        {enableAPIConfig && hasAPISchema && (
          <ViewManager
            views={views}
            currentView={currentView}
            onSwitchView={switchView}
            onCreateView={createView}
            onRemoveView={removeView}
            loading={viewsLoading}
          />
        )}
        <ColumnCustomizer
          columns={gridColumns}
          onVisibilityChange={setVisibility}
          onWidthChange={setWidth}
          onMove={moveColumn}
          onReset={resetToSchema}
          onResetToAPI={hasAPISchema ? resetPreferencesToAPI : undefined}
          onSave={hasAPISchema ? savePreferencesToAPI : undefined}
        />
      </Box>

      {renderToolbar}

      <TableContainer
        sx={{
          position: 'relative',
          width: '100%',
          maxHeight: maxHeight,
          overflow: 'auto',
          scrollbarGutter: 'stable',
          '& .sticky-column-right': {
            position: 'sticky',
            right: 0,
            backgroundColor: '#fff',
            zIndex: 2,
          },
          '& .sticky-header-cell': {
            position: 'sticky',
            top: 0,
            zIndex: 10,
            backgroundColor: '#fff',
            boxShadow: '0 1px 0 rgba(0,0,0,0.1)',
          },
          '& .MuiPopover-root': {
            zIndex: 1400,
          },
          '& .MuiMenu-root': {
            zIndex: 1400,
          },
          '& .MuiAutocomplete-popper': {
            zIndex: 1400,
          },
          '& .MuiSelect-root .MuiMenu-paper': {
            zIndex: 1400,
          },
        }}
      >
        <Table
          sx={{
            tableLayout: 'fixed',
            width: '100%',
            '& th, & td': {
              padding: '8px 12px',
              whiteSpace: 'normal',
              wordWrap: 'break-word',
              verticalAlign: 'top',
              position: 'relative',
            },
            '& th': {
              position: 'relative',
              overflow: 'visible',
            },
            '&.resizing': {
              '& th, & td': {
                transition: 'none',
              },
            },
          }}
          className={table.getState().columnSizingInfo.isResizingColumn ? 'resizing' : ''}
        >
          <TableHead>
            {table.getHeaderGroups().map((headerGroup: any) => (
              <TableRow key={headerGroup.id}>
                {showSelectColumn && (
                  <TableCell
                    align="center"
                    className="sticky-header-cell"
                    sx={{
                      width: selectColumnWidth,
                      minWidth: selectColumnWidth,
                      padding: '8px 4px',
                      position: 'sticky',
                      top: 0,
                      zIndex: 11,
                      backgroundColor: '#fff',
                      boxShadow: '0 1px 0 rgba(0,0,0,0.1)',
                    }}
                  >
                    {selectColumn?.headerCell ?? 'Select'}
                  </TableCell>
                )}
                {headerGroup.headers.map((th: any) => {
                  const header = headerMap.get(th.id);
                  const colSortKey = header?.sortKey ?? th.id;
                  const isSortable = Boolean(onSortChange && header?.sortable !== false);

                  return (
                    <TableCell
                      key={th.id}
                      className="sticky-header-cell"
                      sx={{
                        width: th.getSize(),
                        position: 'sticky',
                        top: 0,
                        userSelect: 'none',
                        overflow: 'visible',
                        zIndex: 3,
                        backgroundColor: '#fff',
                        boxShadow: '0 1px 0 rgba(0,0,0,0.1)',
                        minWidth: th.column.columnDef.minSize || 80,
                        maxWidth: th.column.columnDef.maxSize,
                        '& .MuiBox-root': {
                          minWidth: 0,
                          '& .MuiTableSortLabel-root': {
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          },
                        },
                        // Ensure resize handle area is accessible
                        paddingRight: '16px',
                      }}
                    >
                      {/* Use makeHeader from useTableView for filter UI */}
                      {header ? makeHeader(header) : (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            minWidth: 0,
                          }}
                        >
                          {isSortable ? (
                            <Tooltip title={flexRender(th.column.columnDef.header, th.getContext())} arrow>
                              <TableSortLabel
                                active={sortKey === colSortKey}
                                direction={sortKey === colSortKey ? sortDirection : 'asc'}
                                onClick={() => onSortChange?.(colSortKey)}
                                sx={{
                                  minWidth: 0,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  flex: 1,
                                }}
                              >
                                {flexRender(th.column.columnDef.header, th.getContext())}
                              </TableSortLabel>
                            </Tooltip>
                          ) : (
                            <Tooltip title={flexRender(th.column.columnDef.header, th.getContext())} arrow>
                              <Box
                                sx={{
                                  minWidth: 0,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  flex: 1,
                                }}
                              >
                                {flexRender(th.column.columnDef.header, th.getContext())}
                              </Box>
                            </Tooltip>
                          )}
                        </Box>
                      )}

                      {/* Column resize handle */}
                      <Box
                        onMouseDown={th.getResizeHandler()}
                        onTouchStart={th.getResizeHandler()}
                        sx={{
                          position: 'absolute',
                          right: -2,
                          top: 0,
                          height: '100%',
                          width: 8,
                          cursor: 'col-resize',
                          backgroundColor: th.column.getIsResizing()
                            ? 'primary.main'
                            : 'transparent',
                          '&:hover': {
                            backgroundColor: 'primary.light',
                            opacity: 0.8,
                            '&::after': {
                              content: '""',
                              position: 'absolute',
                              right: '50%',
                              top: '15%',
                              bottom: '15%',
                              width: '2px',
                              backgroundColor: 'primary.main',
                              transform: 'translateX(50%)',
                              borderRadius: '1px',
                            },
                          },
                          '&:active': {
                            backgroundColor: 'primary.main',
                            opacity: 0.9,
                          },
                          userSelect: 'none',
                          touchAction: 'none',
                          zIndex: 5,
                          // Add subtle visual cue on hover
                          transition: 'background-color 0.2s ease',
                        }}
                        onClick={(e) => e.stopPropagation()}
                        title="Drag to resize column"
                      />
                    </TableCell>
                  );
                })}

                {showActions && (
                  <TableCell
                    align="center"
                    className="sticky-column-right sticky-header-cell"
                    sx={{
                      width: renderRowActions ? 380 : 140,
                      minWidth: renderRowActions ? 380 : 140,
                      padding: '8px 12px',
                      position: 'sticky',
                      top: 0,
                      right: 0,
                      zIndex: 11,
                      backgroundColor: '#fff',
                      boxShadow: '0 1px 0 rgba(0,0,0,0.1)',
                    }}
                  >
                    Actions
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableHead>

          <TableBody>
            {table.getRowModel().rows.map((row: any, rowIndex: number) => {
              const actionId = getActionId(row.original, rowIndex);
              return (
                <TableRow
                  hover
                  key={getRowId(row.original, rowIndex)}
                  onClick={() => onRowClick?.(row.original)}
                  sx={{ cursor: onRowClick ? 'pointer' : 'default' }}
                >
                  {showSelectColumn && (
                    <TableCell
                      align="center"
                      sx={{
                        width: selectColumnWidth,
                        minWidth: selectColumnWidth,
                        padding: '4px',
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {selectColumn!.renderCell(row.original, rowIndex)}
                    </TableCell>
                  )}
                  {row.getVisibleCells().map((cell: any) => (
                    <TableCell
                      key={cell.id}
                      sx={{
                        width: cell.column.getSize(),
                        minWidth: cell.column.columnDef.minSize || 80,
                        maxWidth: cell.column.columnDef.maxSize,
                        overflow: 'visible',
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        verticalAlign: 'top',
                        paddingRight: '16px',
                      }}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}

                  {showActions && (
                    <TableCell
                      align="center"
                      className="sticky-column-right"
                      sx={{ width: 140, minWidth: 140, padding: '4px' }}
                    >
                      {renderActions ? (
                        renderActions(row.original, rowIndex)
                      ) : (
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                          {showView && actionId != null && (
                            <Tooltip title="View">
                              <IconButton
                                size="small"
                                onClick={(e) => { e.stopPropagation(); handleView(actionId); }}
                              >
                                <img src={Images.VIEW} alt="View" width="22" />
                              </IconButton>
                            </Tooltip>
                          )}
                                                    {showEdit && actionId != null && (
                            <Tooltip title={isEditDisabled?.(row.original) ? "Cannot edit" : "Edit"}>
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={isEditDisabled?.(row.original)}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (!isEditDisabled?.(row.original)) {
                                      handleEdit(actionId);
                                    }
                                  }}
                                  sx={{
                                    opacity: isEditDisabled?.(row.original) ? 0.4 : 1,
                                    cursor: isEditDisabled?.(row.original) ? 'not-allowed' : 'pointer',
                                  }}
                                >
                                  <img
                                    src={Images.EDIT}
                                    alt="Edit"
                                    width="22"
                                    style={{ opacity: isEditDisabled?.(row.original) ? 0.5 : 1 }}
                                  />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          {showApprovals && actionId != null && (
                            <Tooltip title="Approvals">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={(e) => { e.stopPropagation(); handleApprovals(actionId); }}
                              >
                                <CheckBoxIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {showDelete && actionId != null && (
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={(e) => { e.stopPropagation(); handleDeleteClick(actionId); }}
                              >
                                <img src={Images.DELETE} alt="Delete" width="22" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {renderRowActions?.(row.original)}
                      </Box>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}

            {loading && data.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={visibleGridColumns.length + (showActions ? 1 : 0) + (showSelectColumn ? 1 : 0)}
                  align="center"
                  sx={{ py: 4 }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
                    <CircularProgress size={20} />
                    Loading...
                  </Box>
                </TableCell>
              </TableRow>
            )}

            {!loading && data.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={visibleGridColumns.length + (showActions ? 1 : 0) + (showSelectColumn ? 1 : 0)}
                  align="center"
                >
                  {emptyMessage}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {loading && data.length > 0 && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255,255,255,0.8)',
              zIndex: 4,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, backgroundColor: 'white', padding: 2, borderRadius: 1, boxShadow: 2 }}>
              <CircularProgress size={24} />
              <Box sx={{ color: 'text.secondary' }}>Loading...</Box>
            </Box>
          </Box>
        )}
      </TableContainer>

      {/* Date filter dialog */}
      {activeDateColumn && (
        <Dialog
          open={Boolean(dateFilterKey)}
          onClose={() => setDateFilterKey(null)}
          maxWidth="xs"
        >
          <DialogTitle>Filter by {activeDateColumn.label}</DialogTitle>
          <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="From Date"
                format={DATE_FORMATS.INPUT}
                value={pendingDateRange[0]}
                onChange={(date) => setPendingDateRange([date as Date | null, pendingDateRange[1]])}
                slotProps={{ 
                  textField: { fullWidth: true, size: 'small', placeholder: DATE_FORMATS.INPUT },
                  actionBar: {
                    actions: ['clear'],
                  },
               }}
              />
              <DatePicker
                label="To Date"
                format={DATE_FORMATS.INPUT}
                value={pendingDateRange[1]}
                onChange={(date) => setPendingDateRange([pendingDateRange[0], date as Date | null])}
                slotProps={{ 
                  textField: { fullWidth: true, size: 'small', placeholder: DATE_FORMATS.INPUT },
                  actionBar: {
                    actions: ['clear'],
                  },
                 }}
              />
            </LocalizationProvider>
          </DialogContent>
          <DialogActions>
            <Button
              size="small"
              onClick={() => {
                activeDateColumn.onFilterChange?.(['', '']);
                setPendingDateRange([null, null]);
                setDateFilterKey(null);
              }}
            >
              Clear
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                const nextValue: [string, string] = [
                  pendingDateRange[0] ? format(pendingDateRange[0], 'yyyy-MM-dd') : '',
                  pendingDateRange[1] ? format(pendingDateRange[1], 'yyyy-MM-dd') : '',
                ];
                activeDateColumn.onFilterChange?.(nextValue);
                setDateFilterKey(null);
              }}
            >
              Apply
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Number range filter dialog */}
      {activeNumberRangeColumn && (
        <Dialog
          open={Boolean(numberRangeFilterKey)}
          onClose={() => setNumberRangeFilterKey(null)}
          maxWidth="xs"
        >
          <DialogTitle>Filter by {activeNumberRangeColumn.label}</DialogTitle>
          <DialogContent
            sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}
          >
            <TextField
              label="From"
              type="number"
              size="small"
              fullWidth
              value={pendingNumberRange[0]}
              onChange={(e) => {
                setPendingNumberRange([e.target.value, pendingNumberRange[1]]);
              }}
              placeholder="Enter minimum value"
            />
            <TextField
              label="To"
              type="number"
              size="small"
              fullWidth
              value={pendingNumberRange[1]}
              onChange={(e) => {
                setPendingNumberRange([pendingNumberRange[0], e.target.value]);
              }}
              placeholder="Enter maximum value"
            />
          </DialogContent>
          <DialogActions>
            <Button
              size="small"
              onClick={() => {
                activeNumberRangeColumn.onFilterChange?.(['', '']);
                setPendingNumberRange(['', '']);
                setNumberRangeFilterKey(null);
              }}
            >
              Clear
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                activeNumberRangeColumn.onFilterChange?.(pendingNumberRange);
                setNumberRangeFilterKey(null);
              }}
            >
              Apply
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {confirmDelete && (
        <ConfirmDeleteDialog
          open={deleteDialogOpen}
          title={confirmDelete.title}
          message={confirmDelete.message}
          confirmLabel={confirmDelete.confirmLabel}
          cancelLabel={confirmDelete.cancelLabel}
          disableBackdropClose={confirmDelete.disableBackdropClose}
          disableEscapeKeyDown={confirmDelete.disableEscapeKeyDown}
          onCancel={() => { setDeleteDialogOpen(false); setPendingDeleteId(null); }}
          onConfirm={() => {
            if (pendingDeleteId == null || !handleDelete) return;
            handleDelete(pendingDeleteId);
            setDeleteDialogOpen(false);
            setPendingDeleteId(null);
          }}
        />
      )}

      {!disablePagination && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 , pb: '40px', }}>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(_e, newPage) => onPageChange?.(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => onRowsPerPageChange?.(parseInt(e.target.value, 10))}
            rowsPerPageOptions={rowsPerPageOptions}
          />
        </Box>
      )}
    </Box>
  );
}

const TanStackTableViewWithSchema = forwardRef<TanStackTableViewWithSchemaRef, TanStackTableViewWithSchemaProps<any>>(TanStackTableViewWithSchemaInner);

export default memo(TanStackTableViewWithSchema);
